import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useFormContext } from "@4uhub/lib4uhub";
import { useWatch } from "react-hook-form";
import { WhatsappChat } from "@4uhub/lib4uhub";
import { useTemplateWhats } from "../../../store/contexts/TemplateWhatsContext";
import { tokenService } from "../../../services/token.service";

const translationPath = "chatBot.page.templates.whatsapp_template.";

export const WhatsappMessagePreview = memo(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("lg"));

  const {
    templateWhatsBasicData,
    ctaButtons,
    customButtons,
    buttonsList,
    showAllOptions,
    variables,
    closeAllOptions,
    openAllOptions,
  } = useTemplateWhats();

  const { control } = useFormContext();
  const formCtas = useWatch({ control, name: "ctaButtons" }) ?? [];
  const formCustom = useWatch({ control, name: "customButtons" }) ?? [];
  const mediaFile = useWatch({ control, name: "mediaFile" }) ?? null;

  // 👉 headerType do schema (code: "1" texto, "2" imagem, "3" documento)
  const headerType = useWatch({ control, name: "headerType" }) ?? null;

  const effectiveCtas = (ctaButtons?.length ? ctaButtons : formCtas) ?? [];
  const effectiveCustom =
    (customButtons?.length ? customButtons : formCustom) ?? [];

  const { title, body, footer } = templateWhatsBasicData;

  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const docPlaceholder = useMemo(() => {
    const bg = theme.palette.mode === "light" ? "#BDBDBD" : "#424242";
    const fg = theme.palette.mode === "light" ? "#FFFFFF" : "#EEEEEE";
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360">
        <rect x="0" y="0" width="640" height="360" rx="24" ry="24" fill="${bg}" />
        <g fill="${fg}" opacity="0.92">
          <rect x="270" y="96" width="100" height="130" rx="10" />
          <rect x="285" y="126" width="70" height="10" rx="5" />
          <rect x="285" y="146" width="70" height="10" rx="5" />
          <rect x="285" y="166" width="40" height="10" rx="5" />
        </g>
      </svg>`;
    return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
  }, [theme.palette.mode]);

  useEffect(() => {
    let revokeUrl: string | null = null;

    const buildCdnUrl = (cdn?: string, path?: string) => {
      if (!cdn || !path) return null;
      const left = cdn.replace(/\/+$/, "");
      const right = String(path).replace(/^\/+/, "");
      return `${left}/${right}`;
    };

    const load = async () => {
      if (!headerType?.code) {
        setPreviewSrc(null);
        return;
      }

      if (headerType.code === "4") {
        setPreviewSrc(docPlaceholder);
        return;
      }

      if (headerType.code === "2") {
        if (!mediaFile) {
          setPreviewSrc(null);
          return;
        }

        if (mediaFile.file instanceof File) {
          const obj = URL.createObjectURL(mediaFile.file);
          revokeUrl = obj;
          setPreviewSrc(obj);
          return;
        }

        const cdnUrl = buildCdnUrl(mediaFile.cdnDomain, mediaFile.filePath);
        if (cdnUrl) {
          setPreviewSrc(cdnUrl);
          return;
        }
        console.log(mediaFile);
        if (!mediaFile.filePath && mediaFile.id) {
          try {
            const token = tokenService.getAccessTokens?.();
            const resp = await axios.get(`/files/view/${mediaFile.id}`, {
              responseType: "blob",
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            });
            const obj = URL.createObjectURL(resp.data);
            revokeUrl = obj;
            setPreviewSrc(obj);
          } catch {
            setPreviewSrc(null);
          }
          return;
        }

        setPreviewSrc(null);
        return;
      }

      setPreviewSrc(null);
    };

    load();

    return () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [mediaFile, headerType?.code, docPlaceholder]);

  if (matches) return null;

  const alt =
    headerType?.code === "3"
      ? (mediaFile?.fileName ?? "documento")
      : (mediaFile?.fileName ?? title ?? "imagem");

  return (
    <Grid
      item
      container
      sm={matches ? 12 : 3}
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        backgroundColor: t.palette.grey[t.palette.mode === "light" ? 200 : 800],
        borderRadius: 2,
        boxShadow: "0px 0px 10px -6px rgba(66,66,66,0.6)",
        position: "relative",
      })}
    >
      <Box
        sx={(t) => ({
          padding: 2,
          paddingLeft: 4,
          width: "100%",
          height: "fit-content",
          backgroundColor:
            t.palette.mode === "light"
              ? t.palette.background.default
              : t.palette.grey[900],
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15,
        })}
      >
        <Typography fontWeight="bold">
          {t(translationPath + "message_preview")}
        </Typography>
      </Box>

      <Box sx={{ position: "sticky", top: 118 }}>
        <WhatsappChat
          title={title}
          body={body}
          footer={footer}
          ctaButtons={effectiveCtas}
          customButtons={effectiveCustom}
          buttonsList={buttonsList}
          showAllOptions={showAllOptions}
          onCloseAllOptions={closeAllOptions}
          openAllOptions={openAllOptions}
          variables={variables}
          srcImageUrl={previewSrc || undefined}
          imageAlt={alt}
        />
      </Box>
    </Grid>
  );
});
